<template>
    <Modal v-if="isModalOpen" :isOpen="isModalOpen" title="Topic Messages Filter" size="xl" @update:isOpen="handleClose">
        <template #default>
            <div class="space-y-5">
                <!-- Regex Filter Section -->
                <div class="border-b pb-5">
                    <h3 class="text-md font-semibold mb-3">Seek Filter</h3>
                    <div class="gap-4">
                        <!-- Input Group for Seek Filter -->
                        <div
                            class="flex rounded-md bg-white outline outline-1 -outline-offset-1 outline-gray-300 has-[input:focus-within]:outline has-[input:focus-within]:outline-2 has-[input:focus-within]:-outline-offset-2">
                            <div class="grid shrink-0 grid-cols-1 focus-within:relative">
                                <select
                                    id="seek-type"
                                    name="seek-type"
                                    autocomplete="seek-type"
                                    v-model="seekType"
                                    aria-label="Seek Type"
                                    class="col-start-1 row-start-1 w-full appearance-none rounded-md py-1.5 pl-3 pr-7 text-base text-gray-500 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6">
                                    <option v-for="option in seekTypeOptions" :key="option.value" :value="option.value">
                                        {{ option.name }}
                                    </option>
                                </select>
                                <ChevronDownIcon
                                    class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                                    aria-hidden="true" />
                            </div>
                            <template v-if="seekType === 'offset'">
                                <input
                                    type="number"
                                    v-model.number="seekValue"
                                    placeholder="Enter offset"
                                    class="block min-w-0 grow py-1.5 pl-1 pr-3 text-base text-gray-900 placeholder:text-gray-400 focus:outline focus:outline-0 sm:text-sm/6" />
                            </template>
                            <template v-else-if="seekType === 'timestamp'">
                                <VueDatePicker
                                    v-model="seekValue"
                                    :start-date="new Date()"
                                    placeholder="Select timestamp"
                                    mode="single"
                                    :timezone="'UTC'"
                                    class="block min-w-0 grow text-base text-gray-900 placeholder:text-gray-400 focus:outline focus:outline-0 sm:text-sm/6" />
                            </template>
                        </div>
                        <div v-if="seekError" class="text-red-500 text-xs mt-1">{{ seekError }}</div>
                    </div>
                </div>

                <div>
                    <h3 class="text-md font-semibold mb-3">Regex Filter</h3>
                    <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-1"> Search In: </label>
                        <multiselect
                            v-model="selectedFields"
                            :options="fieldOptions"
                            :searchable="false"
                            :multiple="true"
                            placeholder="Select fields"
                            label="name"
                            track-by="name"
                            class="w-full" />
                        <div v-if="fieldError" class="text-red-500 text-xs mt-1">
                            {{ fieldError }}
                        </div>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1"> Regex Pattern: </label>
                        <codemirror v-model="pattern" :options="codeMirrorOptions" class="w-full border rounded-sm" />
                        <div v-if="patternError" class="text-red-500 text-xs mt-1">
                            {{ patternError }}
                        </div>
                    </div>

                    <div v-if="selectedFields.length && pattern?.length" class="rounded-md bg-yellow-50 p-4 mt-4">
                        <div class="flex">
                            <div class="text-xs text-yellow-700 flex gap-2">
                                <div class="shrink-0">
                                    <ExclamationTriangleIcon class="size-5 text-yellow-400" aria-hidden="true" />
                                </div>
                                <p class="text-xs">
                                    Regex search is not natively supported by Kafka and requires scanning messages in a less performant way. This may
                                    take longer or timeout if the topic contains a large number of messages.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Seek Filter Section -->
            </div>
        </template>
        <template #footer>
            <div class="flex justify-end gap-2">
                <button class="bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-md py-2 px-4 mb-2 w-full min-w-[88px]" @click="handleCancel">
                    Cancel
                </button>
                <button
                    class="rounded-md py-2 px-4 mb-2 w-full min-w-[88px] bg-cyan-950 hover:bg-cyan-900 text-white"
                    :disabled="!isFormValid"
                    @click="applyFilter">
                    Apply
                </button>
            </div>
        </template>
    </Modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import Modal from '@/components/global/modals/Modal.vue';
import Multiselect from 'vue-multiselect';
import { Codemirror } from 'vue-codemirror';
import VueDatePicker from '@vuepic/vue-datepicker';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/vue/20/solid';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    initialFilter: {
        type: Object,
        default: () => ({
            regex: { fields: [], pattern: '' },
            seek: { seekType: null, seekValue: null }
        })
    }
});
const emit = defineEmits(['apply', 'update:isOpen']);

const isModalOpen = ref(props.isOpen);

const seekType = ref(props.initialFilter?.seek?.seekType ?? null);
const seekValue = ref(props.initialFilter?.seek?.seekValue ?? null);
const seekTypeOptions = ref([
    { name: 'Offset', value: 'offset' },
    { name: 'Timestamp', value: 'timestamp' }
]);
const seekError = computed(() => {
    if (seekType.value && (seekValue.value === null || seekValue.value === '')) {
        return 'Please provide a value for the seek filter.';
    }
    return '';
});

watch(
    () => seekType.value,
    (newVal) => {
        if (newVal === 'timestamp') {
            if (props.initialFilter?.seek?.seekValue) {
                if (props.initialFilter?.seek?.seekType === 'timestamp') {
                    seekValue.value = new Date(props.initialFilter?.seek?.seekValue);
                } else {
                    seekValue.value = new Date();
                }
            } else {
                seekValue.value = new Date();
            }
        } else if (newVal === 'offset') {
            if (props.initialFilter?.seek?.seekValue) {
                if (props.initialFilter?.seek?.seekType === 'offset') {
                    seekValue.value = props.initialFilter?.seek?.seekValue;
                } else {
                    seekValue.value = null;
                }
            } else {
                seekValue.value = null;
            }
        }
    }
);

const fieldOptions = [{ name: 'key' }, { name: 'value' }];
const selectedFields = ref((props.initialFilter?.regex?.fields || []).map((field) => ({ name: field })));
const pattern = ref(props.initialFilter?.regex?.pattern || '');
const codeMirrorOptions = {
    mode: 'javascript',
    lineNumbers: false,
    theme: 'base16-dark',
    viewportMargin: Infinity,
    lineWrapping: true
};
const fieldError = computed(() => {
    if (selectedFields.value.length === 0 && pattern.value.trim() !== '') {
        return 'At least one field must be selected.';
    }
    return '';
});
const patternError = computed(() => {
    if (pattern.value.trim() === '' && selectedFields.value.length > 0) {
        return 'Regex pattern must not be empty.';
    }
    if (pattern.value.trim() !== '') {
        try {
            new RegExp(pattern.value);
            return '';
        } catch (e) {
            return 'Invalid regex pattern.';
        }
    }
    return '';
});

const isSeekValid = computed(() => seekType.value && seekValue.value !== null && seekError.value === '');

const isRegexValid = computed(() => {
    return selectedFields.value.length > 0 && pattern.value && pattern.value?.trim() !== '' && patternError.value === '';
});

// --- Overall Form Validation ---
const isFormValid = computed(() => {
    return isSeekValid.value || isRegexValid.value;
});

const applyFilter = () => {
    if (!isFormValid.value) return;

    const payload = {};

    if (isSeekValid.value) {
        payload.seek = {
            seekType: seekType.value,
            seekValue: seekType.value === 'timestamp' && seekValue.value ? new Date(seekValue.value).getTime() : seekValue.value
        };
    }

    if (isRegexValid.value) {
        payload.regex = {
            fields: selectedFields.value.map((item) => item.name),
            pattern: pattern.value?.trim()
        };
    }

    emit('apply', payload);
};

const handleClose = (value) => {
    isModalOpen.value = value;
    if (!value) {
        emit('update:isOpen', false);
    }
};

const handleCancel = () => {
    emit('update:isOpen', false);
};

watch(
    () => props.initialFilter,
    (newVal) => {
        selectedFields.value = (newVal?.regex?.fields || []).map((field) => ({ name: field }));
        pattern.value = newVal?.regex?.pattern || '';
        seekType.value = newVal?.seek?.seekType ?? 'offset';
        seekValue.value = newVal?.seek?.seekValue;
    },
    { immediate: true }
);
</script>
<style scoped></style>
